import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { authApi, organizationApi } from '@/lib/pillarApi';
import {
    getOrganizationCapacityLabel,
    organizationHasCapacity,
} from '@/lib/organization-capacity';
import { runInBackground } from '@/lib/utils';
import { isoSchema, type IsoFormData } from '@/lib/validation-organization';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    type ActiveRoleAssignmentResponse,
    type OrganizationOmnisearchListItem,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';

function CreateIsoContent() {
    const log = createComponentLogger('CreateIsoPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const bumpOrganizationListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpOrganizationListsVersion
    );

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            enabled: !!user,
            staleTime: 30000,
        });
    const canManageOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );

    const parentIsoUuidFromUrl = searchParams.get('parent_iso_uuid');
    const isParentIsoLocked = !!parentIsoUuidFromUrl;

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<IsoFormData>({
        resolver: zodResolver(isoSchema),
    });

    const createIsoMutation = useMutation({
        mutationFn: organizationApi.createIso,
        onSuccess: (data) => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizationListing'], exact: false }),
                (err) => {
                    log.error('Error invalidating organization listing queries:', err);
                }
            );
            // New org can be hidden by the accessible-org filter if this query is cached.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: ['authorization', 'accessible_organization_ids'],
                }),
                (err) => {
                    log.error('Error invalidating accessible org ids query:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/organizations/${data.organization_uuid}`,
                    title: 'Organization Details',
                },
            });
        },
    });

    const onSubmit = (data: IsoFormData) => {
        if (!canManageOrganizations) {
            throw new Error('Access denied: insufficient permissions to create ISO');
        }
        createIsoMutation.mutate({
            name: data.name,
            ...(data.parent_organization_uuid && {
                parent_organization_uuid: data.parent_organization_uuid,
            }),
        });
    };

    const pageTitle = parentIsoUuidFromUrl
        ? `Create ISO under ${parentIsoUuidFromUrl}`
        : 'Create ISO';

    if (isActiveRoleAssignmentLoading) {
        return <div>Loading...</div>;
    }
    if (!canManageOrganizations) {
        return (
            <div>
                <BackButton href="/dashboard" tooltip="Back to Dashboard" />
                <div className="text-red-600">Access denied. Insufficient permissions.</div>
            </div>
        );
    }

    return (
        <div>
            <BackButton
                href={
                    parentIsoUuidFromUrl
                        ? `/organizations/${parentIsoUuidFromUrl}`
                        : '/organizations/isos'
                }
                tooltip={parentIsoUuidFromUrl ? 'Back to ISO' : 'Back to Organizations'}
            />
            <div className="flex items-center gap-4 mb-6">
                <h1 className="text-3xl font-bold">{pageTitle}</h1>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>ISO Information</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div>
                            <Label htmlFor="name">ISO Name</Label>
                            <Input id="name" {...register('name')} placeholder="Enter ISO name" />
                            {errors.name && (
                                <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="parent_organization_uuid">Parent ISO (Optional)</Label>
                            {isParentIsoLocked ? (
                                <p className="text-sm text-muted-foreground mt-2">
                                    Parent ISO is locked to:{' '}
                                    <span className="font-medium">{parentIsoUuidFromUrl}</span>
                                </p>
                            ) : (
                                <div className="mt-2">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search parent ISOs"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={createIsoMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_iso: true,
                                                });
                                            return {
                                                items: response.organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) => `${item.name} ${item.organization_uuid}`}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_iso')) {
                                                throw new Error(
                                                    `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            setValue('parent_organization_uuid', item.organization_uuid);
                                        }}
                                    />
                                </div>
                            )}
                            {isParentIsoLocked && (
                                <p className="text-sm text-muted-foreground">
                                    Parent ISO is locked because you are creating an ISO under a
                                    specific parent.
                                </p>
                            )}
                            {errors.parent_organization_uuid && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.parent_organization_uuid.message}
                                </p>
                            )}
                        </div>

                        {createIsoMutation.error && (
                            <div className="text-red-600 text-sm">
                                Error: {String(createIsoMutation.error)}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <Button type="submit" disabled={createIsoMutation.isPending}>
                                {createIsoMutation.isPending ? 'Creating...' : 'Create ISO'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CreateIsoPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CreateIsoContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
